import React, { memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import {
  getParents,
  getUniqueKey,
  useMergedClassNames,
} from '../../utils/HTMLutils';
import './FloatingPanel.scss';
const rootClassName = 'floating-panel';

export interface FloatingPanelProps {
  opened: boolean;
  setOpened: (evt: boolean) => void;
  children: ReactNode | JSX.Element;
  className?: string;
  bottom?: string;
  radiusStyle?: 'only-top' | 'only-bottom' | 'all';
  boxShadow?: boolean;
}

function FloatingPanel(props: FloatingPanelProps) {
  const id = useMemo(getUniqueKey, []);

  // Window Outside Click
  const onClick = useCallback(
    (evt: MouseEvent) => {
      // console.log('onClick');
      const wrapper = document.getElementById(id);
      if (!wrapper) return;

      if (!evt.composedPath().includes(wrapper)) {
        props.setOpened(false);
        evt.preventDefault();
      }
    },
    [id, props]
  );

  const onEsc = useCallback(
    (evt: KeyboardEvent) => {
      // console.log('onKey', evt.key);
      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        props.setOpened(false);
      }
    },
    [props]
  );

  // Window Scroll
  const updatePosition = useCallback(() => {
    // console.log('updatePosition');
    const wrapper = document.getElementById(id);
    if (!wrapper) return;
    const computedStyle = wrapper.getBoundingClientRect();
    const panel = document.querySelector(
      `#${id} .${rootClassName}`
    ) as HTMLElement;
    if (!panel) return;
    panel.style.top = `${computedStyle.top + computedStyle.height}px`;
    panel.style.left = `${computedStyle.left}px`;
  }, [id]);

  //  Resize
  const updateSize = useCallback(() => {
    // console.log('updateSize');
    const anchor = document.getElementById(id)?.parentElement;
    const panel = document.querySelector(
      `#${id} .${rootClassName}`
    ) as HTMLElement;
    if (!panel || !anchor) return;
    const computedStyle = window.getComputedStyle(anchor);
    panel.style.width = computedStyle.width;
  }, [id]);

  useEffect(() => {
    const wrapper = document.getElementById(id);
    // console.log('wrapper: ', wrapper);
    const scrollableElements = getParents(wrapper);
    // console.log('scrollableElements: ', scrollableElements);
    const positionAndSize = () => {
      // console.log('positionAndSize');
      updatePosition();
      updateSize();
    };

    const observer = new window.MutationObserver(positionAndSize);
    let timeout: NodeJS.Timeout;
    if (props.opened) {
      positionAndSize();
      observer.observe(document.body, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
      for (const el of scrollableElements) {
        el.addEventListener('scroll', updatePosition);
      }
      timeout = setTimeout(() => {
        window.addEventListener('resize', positionAndSize);
        document.addEventListener('keydown', onEsc);
        document.addEventListener('click', onClick);
      }, 300);
    }
    //else {
    //   console.log('closed');
    // }
    return () => {
      if (timeout) clearTimeout(timeout);
      if (props.opened) {
        // console.log('return opened');
        observer.disconnect();
        document.removeEventListener('keydown', onEsc);
        document.removeEventListener('click', onClick);
        window.removeEventListener('resize', positionAndSize);
        for (const el of scrollableElements) {
          el.removeEventListener('scroll', updatePosition);
        }
      } // else {
      // console.log('return closed');
      // }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.opened]);
  return (
    <div
      id={id}
      className={`${rootClassName}-wrapper`}
      style={{ bottom: props.bottom }}
    >
      <CSSTransition
        in={props.opened}
        classNames={rootClassName}
        timeout={300}
        unmountOnExit
      >
        <div
          className={useMergedClassNames([
            rootClassName,
            props.className,
            props.boxShadow ? 'shadow' : '',
            props.radiusStyle,
          ])}
        >
          {props.children}
        </div>
      </CSSTransition>
    </div>
  );
}

FloatingPanel.defaultProps = {
  boxShadow: true,
  radiusStyle: 'only-bottom',
} as Partial<FloatingPanelProps>;

export default memo(FloatingPanel);
